import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import CitizenAdvisorShell from '../../components/citizen/CitizenAdvisorShell';
import {
  PERMIT_PUBLIC_DEMO_ID,
  loadPermitPublicDemoDefinition,
  permitPublicDemoDefinition,
} from '../../data/interviewFlowPermitDemo';
import {
  fetchJurisdictionPackage,
  getFallbackJurisdictionPackage,
  portalsFromPackage,
} from '../../data/sfAdvisorIntake';
import useResolvedJurisdiction from '../../hooks/useResolvedJurisdiction';
import { trackDiscovery } from '../../utils/discoverySession';
import {
  defaultsFromSearchParams,
  packageIdFromSearchParams,
} from '../../utils/citizenAdvisorQueryPrefill';
import {
  advisorDefaultsFromSession,
  advisorRestoredOutputFromSession,
} from '../../utils/discoverySessionHydrate';
import { intakePathForSession } from '../../utils/persistDiscoverySession';

const PORTAL_LINKS_BASE = [
  { to: '/v2/demo/permit', label: 'Advisor', id: 'default' },
  { to: '/v2/demo/permit/residential', label: 'Residential', id: 'residential' },
  { to: '/v2/demo/permit/business', label: 'Business', id: 'business' },
  { to: '/v2/demo/permit/events', label: 'Events', id: 'events' },
];

/**
 * Public citizen advisor demo — city shell around the Studio V2 / CLAW runtime.
 * On vanity hosts, package comes from resolve-host (not hard-coded SF).
 */
export default function StudioV2PermitDemoPage({
  basePath = '/v2/demo/permit',
  defaultPackageId = 'san-francisco',
} = {}) {
  const [searchParams] = useSearchParams();
  const queryDefaults = defaultsFromSearchParams(searchParams);
  const queryCity = packageIdFromSearchParams(searchParams);
  // Only hydrate from an explicit ?session= (history restore). Auto-minted ids must not
  // keep the left rail on checklist after New request.
  const sessionId = searchParams.get('session')
    || searchParams.get('sessionId')
    || '';
  const resolved = useResolvedJurisdiction({ fallbackPackageId: defaultPackageId });
  // Explicit ?city= wins (multi-city demos on one App). Else vanity Host, else default.
  const packageId = queryCity
    || (resolved.matched ? resolved.packageId : null)
    || resolved.packageId
    || defaultPackageId;

  // Only seed SF static definition when not switching cities / restoring session
  const [definition, setDefinition] = useState(() => (
    !queryCity
    && !sessionId
    && defaultPackageId === 'san-francisco'
    && !Object.keys(queryDefaults).length
      ? permitPublicDemoDefinition
      : null
  ));
  const [pkg, setPkg] = useState(() => getFallbackJurisdictionPackage(queryCity || defaultPackageId));

  useEffect(() => {
    if (resolved.loading) return undefined;
    let cancelled = false;
    (async () => {
      let session = null;
      if (sessionId) {
        try {
          const { getDiscoverySessionApi } = await import('../../api/applicationType.api.js');
          session = await getDiscoverySessionApi(sessionId);
        } catch (_) {
          session = null;
        }
      }
      if (cancelled) return;

      const sessionDefaults = advisorDefaultsFromSession(session);
      const defaults = {
        ...sessionDefaults,
        ...queryDefaults,
      };
      const restored = advisorRestoredOutputFromSession(session);

      try {
        const loaded = await fetchJurisdictionPackage(packageId);
        if (cancelled) return;
        setPkg(loaded);
        const def = await loadPermitPublicDemoDefinition({
          jurisdictionId: loaded.packageId,
          pkg: loaded,
          defaults: Object.keys(defaults).length ? defaults : undefined,
        });
        if (def?.metadata?.interviewFlow) {
          if (Object.keys(defaults).length) {
            def.metadata.interviewFlow.initialFormPrefill = {
              ...(def.metadata.interviewFlow.initialFormPrefill || {}),
              ...defaults,
            };
          }
          if (restored) {
            def.metadata.interviewFlow.restoreAdviseResult = true;
            def.metadata.interviewFlow.previewStructuredOutput = restored;
            def.metadata.interviewFlow.initialPhaseId = 'checklist';
            def.metadata.interviewFlow.previewPanelOutputs = {
              ...(def.metadata.interviewFlow.previewPanelOutputs || {}),
              left_checklist: {
                table_data: {
                  columns: restored.checklist?.columns || [],
                  rows: restored.checklist?.rows || [],
                },
              },
              left_summary: {
                rich_text: { content: restored.summary?.content || '' },
              },
            };
          } else {
            def.metadata.interviewFlow.restoreAdviseResult = false;
            def.metadata.interviewFlow.initialPhaseId = 'advise';
          }
        }
        if (!cancelled && def) setDefinition(def);
        trackDiscovery('portal_view', { packageId: loaded.packageId, portalId: 'default' });
      } catch (_) {
        /* keep fallback definition */
      }
    })();
    return () => { cancelled = true; };
  }, [packageId, resolved.loading, searchParams, sessionId]);

  const demoQueryKeys = [
    'address', 'location', 'description', 'proposedUse', 'use',
    'projectType', 'propertyType', 'foodHandling', 'food',
    'servesAlcohol', 'alcohol', 'outdoorSeating', 'outdoor',
    'autoAdvise', 'advise', 'type', 'applicationTypeId',
  ];
  const appendDemoQuery = (params) => {
    demoQueryKeys.forEach((key) => {
      const v = searchParams.get(key);
      if (v != null && String(v).trim() && !params.has(key)) params.set(key, v);
    });
  };
  const qs = (() => {
    const p = new URLSearchParams();
    if (queryCity) p.set('city', queryCity);
    if (sessionId) p.set('session', sessionId);
    appendDemoQuery(p);
    const s = p.toString();
    return s ? `?${s}` : '';
  })();

  const pathFor = (portalId) => {
    const base = (portalId === 'default' || !portalId) ? basePath : `${basePath}/${portalId}`;
    return `${base}${qs}`;
  };

  const portalLinks = PORTAL_LINKS_BASE.map((p) => ({
    ...p,
    to: pathFor(p.id === 'default' ? null : p.id),
    active: p.id === 'default',
  }));

  const fromPkg = Object.values(portalsFromPackage(pkg) || {});
  const withQs = (to) => {
    if (!to) return to;
    const p = new URLSearchParams();
    if (queryCity && !String(to).includes('city=')) p.set('city', queryCity);
    if (sessionId && !String(to).includes('session=')) p.set('session', sessionId);
    appendDemoQuery(p);
    // Avoid duplicating keys already on `to`
    const existing = new URLSearchParams(String(to).split('?')[1] || '');
    existing.forEach((_, k) => p.delete(k));
    const extra = p.toString();
    if (!extra) return to;
    const sep = String(to).includes('?') ? '&' : '?';
    return `${to}${sep}${extra}`;
  };
  const hasLiveZoning = (pkg?.parcelAdapter || pkg?.intake?.parcelAdapter) === 'sf-planning-gis'
    || packageId === 'san-francisco';
  const intakeHref = intakePathForSession({
    sessionId,
    city: queryCity || packageId,
    packageId,
  });
  const links = [
    ...(fromPkg.length
      ? [
        { to: pathFor(null), label: 'Advisor', active: true },
        ...fromPkg.map((v) => ({
          to: withQs(
            basePath.startsWith('/advisor')
              ? pathFor(v.id)
              : (v.path || pathFor(v.id)),
          ),
          label: v.label,
          active: false,
        })),
      ]
      : portalLinks),
    { to: intakeHref, label: 'Intake', active: false },
    ...(hasLiveZoning
      ? [{ to: withQs('/zoning-check'), label: 'Zoning', active: false }]
      : []),
  ];

  if (resolved.loading || !definition) {
    return (
      <Box sx={{
        height: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
      >
        <CircularProgress size={32} />
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100dvh', maxHeight: '100dvh', overflow: 'hidden' }}>
      <CitizenAdvisorShell
        definition={definition}
        jurisdictionPackage={pkg}
        mongoId={definition.id || PERMIT_PUBLIC_DEMO_ID}
        portalLinks={links}
      />
    </Box>
  );
}
